import {Client} from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import type {
	DiagramWsClient,
	DiagramWsInbound,
	ModelPatchDto,
	CatalogChangeDto,
	DiagramCursorOutbound,
	DiagramLivePayload,
	DiagramLockPayload,
	DiagramAckPayload,
} from '@/types/api';
import type {StompFrameHeaders} from '@/types/api';
import {toWebSocket} from '@/types/api';

export type {ModelPatchDto};

const getSockJsUrl = (): string => {
	if (typeof window === 'undefined') return '';
	const origin = window.location.origin;
	return `${origin}/diagram-ws`;
};

function parseInbound(raw: string): DiagramWsInbound | null {
	try {
		const body = JSON.parse(raw) as Record<string, unknown>;
		if (body?.type === 'navigate' && body.navigate && typeof body.navigate === 'object') {
			const n = body.navigate as Record<string, unknown>;
			return {
				kind: 'navigate',
				navigate: {
					diagramId: Number(n.diagramId),
					structuralVersion: Number(n.structuralVersion),
					historyStackCursor: Number(n.historyStackCursor),
					historyStackSize: Number(n.historyStackSize),
					modelJson: n.modelJson != null ? String(n.modelJson) : null,
				},
			};
		}
		if (body?.type === 'catalog' && Array.isArray(body.catalog)) {
			return {kind: 'catalog', catalog: body.catalog as CatalogChangeDto[]};
		}
		if (body?.type === 'cursor' && body.cursor && typeof body.cursor === 'object') {
			const c = body.cursor as Record<string, unknown>;
			const sessionId = c.clientSessionId != null ? String(c.clientSessionId) : '';
			if (!sessionId) return null;
			return {
				kind: 'cursor',
				cursor: {
					clientSessionId: sessionId,
					userId: c.userId != null ? Number(c.userId) : 0,
					userName: c.userName != null ? String(c.userName) : '',
					x: c.x != null ? Number(c.x) : undefined,
					y: c.y != null ? Number(c.y) : undefined,
					viewId: c.viewId != null ? String(c.viewId) : null,
					left: Boolean(c.left),
				},
			};
		}
		if (body?.type === 'live' && body.live && typeof body.live === 'object') {
			const live = parseLive(body.live as Record<string, unknown>);
			return live ? {kind: 'live', live} : null;
		}
		if (body?.type === 'lock' && body.lock && typeof body.lock === 'object') {
			const lock = parseLock(body.lock as Record<string, unknown>);
			return lock ? {kind: 'lock', lock} : null;
		}
		if (body?.type === 'ack' && body.ack && typeof body.ack === 'object') {
			const ack = parseAck(body.ack as Record<string, unknown>);
			return ack ? {kind: 'ack', ack} : null;
		}
		if (body?.type === 'patch' && body.patch && typeof body.patch === 'object') {
			const patch = body.patch as ModelPatchDto;
			const sv = body.structuralVersionAfterPatch;
			const cur = body.historyStackCursorAfterPatch;
			const sz = body.historyStackSizeAfterPatch;
			const historyActionId =
				typeof body.historyActionId === 'string' && body.historyActionId
					? body.historyActionId
					: undefined;
			return {
				kind: 'patch',
				patch,
				structuralVersion: sv != null ? Number(sv) : undefined,
				historyStackCursor: cur != null ? Number(cur) : undefined,
				historyStackSize: sz != null ? Number(sz) : undefined,
				historyActionId,
			};
		}
		if (Array.isArray((body as ModelPatchDto)?.patches)) {
			return {kind: 'patch', patch: body as ModelPatchDto};
		}
	} catch {
		return null;
	}
	return null;
}

function parseBendpoints(raw: unknown): DiagramLivePayload['bendpoints'] {
	if (!Array.isArray(raw)) return undefined;
	return raw
		.map(item => {
			if (!item || typeof item !== 'object') return null;
			const p = item as Record<string, unknown>;
			const x = Number(p.x);
			const y = Number(p.y);
			if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
			return {x, y};
		})
		.filter((p): p is {x: number; y: number} => p != null);
}

function parseLive(raw: Record<string, unknown>): DiagramLivePayload | null {
	const objectId = raw.objectId != null ? String(raw.objectId) : '';
	const sessionId = raw.clientSessionId != null ? String(raw.clientSessionId) : '';
	if (!objectId || !sessionId) return null;
	return {
		clientSessionId: sessionId,
		userId: raw.userId != null ? Number(raw.userId) : undefined,
		userName: raw.userName != null ? String(raw.userName) : undefined,
		objectId,
		kind: raw.kind != null ? String(raw.kind) : undefined,
		viewIndex: raw.viewIndex != null ? Number(raw.viewIndex) : undefined,
		viewId: raw.viewId != null ? String(raw.viewId) : null,
		x: raw.x != null ? Number(raw.x) : undefined,
		y: raw.y != null ? Number(raw.y) : undefined,
		w: raw.w != null ? Number(raw.w) : undefined,
		h: raw.h != null ? Number(raw.h) : undefined,
		bendpoints: parseBendpoints(raw.bendpoints),
	};
}

function parseLock(raw: Record<string, unknown>): DiagramLockPayload | null {
	const action = raw.action != null ? String(raw.action) : '';
	if (!action) return null;
	return {
		action,
		objectId: raw.objectId != null ? String(raw.objectId) : null,
		kind: raw.kind != null ? String(raw.kind) : null,
		clientSessionId: raw.clientSessionId != null ? String(raw.clientSessionId) : null,
		userId: raw.userId != null ? Number(raw.userId) : undefined,
		userName: raw.userName != null ? String(raw.userName) : undefined,
		pendingPersist: raw.pendingPersist != null ? Boolean(raw.pendingPersist) : undefined,
		viewIndex: raw.viewIndex != null ? Number(raw.viewIndex) : undefined,
		viewId: raw.viewId != null ? String(raw.viewId) : null,
		x: raw.x != null ? Number(raw.x) : undefined,
		y: raw.y != null ? Number(raw.y) : undefined,
		w: raw.w != null ? Number(raw.w) : undefined,
		h: raw.h != null ? Number(raw.h) : undefined,
		bendpoints: parseBendpoints(raw.bendpoints),
		locks: Array.isArray(raw.locks)
			? raw.locks
					.map(item =>
						item && typeof item === 'object' ? parseLock(item as Record<string, unknown>) : null
					)
					.filter((item): item is DiagramLockPayload => item != null)
			: undefined,
	};
}

function parseAck(raw: Record<string, unknown>): DiagramAckPayload | null {
	const status = raw.status != null ? String(raw.status) : '';
	if (!status) return null;
	return {
		clientPatchId: raw.clientPatchId != null ? String(raw.clientPatchId) : null,
		clientSessionId: raw.clientSessionId != null ? String(raw.clientSessionId) : null,
		status,
		structuralVersion: raw.structuralVersion != null ? Number(raw.structuralVersion) : null,
		historyStackCursor: raw.historyStackCursor != null ? Number(raw.historyStackCursor) : null,
		historyStackSize: raw.historyStackSize != null ? Number(raw.historyStackSize) : null,
	};
}

/**
 * WebSocket-клиент для диаграммы: подключение, подписка на топик, отправка макро-патчей.
 */
export function createDiagramWsClient(diagramId: number): DiagramWsClient {
	let client: Client | null = null;
	let listeners: Array<(msg: DiagramWsInbound) => void> = [];
	let connected = false;
	let disconnected = false;
	let publishFailed = false;
	const pendingPatches: ModelPatchDto[] = [];

	const topic = `/topic/diagram/${diagramId}`;
	const destination = `/app/diagram/${diagramId}/patch`;
	const cursorDestination = `/app/diagram/${diagramId}/cursor`;
	const liveDestination = `/app/diagram/${diagramId}/live`;
	const lockDestination = `/app/diagram/${diagramId}/lock`;

	const tryFlushPending = () => {
		if (!client?.active || publishFailed) return;
		while (pendingPatches.length > 0) {
			const patch = pendingPatches.shift()!;
			try {
				client.publish({destination, body: JSON.stringify(patch)});
				console.log('[DiagramWs] патч отправлен из очереди:', patch.patches?.length, 'ops');
			} catch {
				pendingPatches.unshift(patch);
				publishFailed = true;
				return;
			}
		}
	};

	const connect = (): Promise<void> => {
		if (client?.active) return Promise.resolve();
		return new Promise((resolve, reject) => {
			client = new Client({
				webSocketFactory: () => toWebSocket(new SockJS(getSockJsUrl())),
				connectHeaders: {},
				debug: () => {},
				reconnectDelay: 3000,
				heartbeatIncoming: 0,
				heartbeatOutgoing: 0,
				onDisconnect: () => {
					connected = false;
					publishFailed = true;
				},
				onConnect: () => {
					connected = true;
					publishFailed = false;
					console.log('[DiagramWs] подключён, diagramId=', diagramId);
					client?.subscribe(topic, frame => {
						try {
							const raw =
								typeof frame.body === 'string'
									? frame.body
									: JSON.stringify(frame.body);
							const inbound = parseInbound(raw);
							if (inbound) {
								listeners.forEach(fn => fn(inbound));
							} else {
								console.warn('[DiagramWs] неизвестное сообщение');
							}
						} catch (e) {
							console.warn('[DiagramWs] parse frame error:', e, 'body=', frame.body);
						}
					});
					tryFlushPending();
					resolve();
				},
				onStompError: frame => {
					console.error('[DiagramWs] STOMP error:', frame);
					const msg = (frame.headers as StompFrameHeaders)?.message;
					reject(new Error(msg || 'STOMP error'));
				},
			});
			client.activate();
		});
	};

	connect().catch(err => {
		connected = false;
		console.warn('[DiagramWs] подключение не удалось:', err?.message || err);
	});

	return {
		sendPatch(patch: ModelPatchDto) {
			if (disconnected) return;
			if (!patch?.patches?.length) return;
			if (publishFailed || !client?.active) {
				pendingPatches.push(patch);
				return;
			}
			try {
				client!.publish({
					destination,
					body: JSON.stringify(patch),
				});
				console.log('[DiagramWs] патч отправлен:', patch.patches.length, 'ops');
			} catch (e) {
				publishFailed = true;
				pendingPatches.push(patch);
				console.warn('[DiagramWs] соединение разорвано, патч сохранится после reconnect');
			}
		},
		sendCursor(cursor: DiagramCursorOutbound & {clientSessionId: string}) {
			if (disconnected || !cursor?.clientSessionId) return;
			if (!client?.active || publishFailed) return;
			try {
				client.publish({
					destination: cursorDestination,
					body: JSON.stringify(cursor),
				});
			} catch {
				// ephemeral: drop if the socket is gone
			}
		},
		sendLive(live: DiagramLivePayload) {
			if (disconnected || !live?.clientSessionId || !live?.objectId) return;
			if (!client?.active || publishFailed) return;
			try {
				client.publish({
					destination: liveDestination,
					body: JSON.stringify(live),
				});
			} catch {
				// ephemeral: drop if the socket is gone
			}
		},
		sendLock(lock: DiagramLockPayload) {
			if (disconnected || !lock?.action) return;
			if (!client?.active || publishFailed) return;
			try {
				client.publish({
					destination: lockDestination,
					body: JSON.stringify(lock),
				});
			} catch {
				// ephemeral: drop if the socket is gone
			}
		},
		subscribe(onMessage: (msg: DiagramWsInbound) => void) {
			listeners.push(onMessage);
			return () => {
				listeners = listeners.filter(l => l !== onMessage);
			};
		},
		disconnect() {
			disconnected = true;
			if (client) {
				try {
					client.deactivate();
				} catch {
					// ignore
				}
				client = null;
			}
			connected = false;
			listeners = [];
		},
		isConnected: () => connected && (client?.active ?? false),
	};
}
